import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, localBusinessSchema, siteUrl } from "@/lib/schemas";
import { MapPin, Phone, Mail, Star, CheckCircle, ArrowRight, Building2, Heart, Car, Plane } from "lucide-react";

const cityData: Record<string, { name: string; state: string; description: string; services: string[] }> = {
  delhi: { name: "Delhi", state: "Delhi NCT", description: "India's capital and a major financial hub. ExpertAdvise.in provides comprehensive insurance and financial advisory services to residents and businesses across Delhi NCR.", services: ["Health Insurance", "Term Insurance", "Car Insurance", "Corporate Insurance", "Mutual Funds"] },
  noida: { name: "Noida", state: "Uttar Pradesh", description: "A thriving IT and business hub with a young, affluent population. We help Noida residents secure their families with tailored insurance and investment plans.", services: ["Health Insurance", "Term Insurance", "Group Health Insurance", "SIP Investments", "Financial Planning"] },
  gurgaon: { name: "Gurgaon", state: "Haryana", description: "Gurgaon's corporate workforce needs smart financial planning. From corporate group health to personal retirement plans, we've got Gurgaon covered.", services: ["Corporate Insurance", "Health Insurance", "Retirement Planning", "Mutual Funds", "Term Insurance"] },
  mumbai: { name: "Mumbai", state: "Maharashtra", description: "The financial capital of India. ExpertAdvise.in serves Mumbai's diverse population with customized insurance solutions for individuals, families, and businesses.", services: ["Health Insurance", "Life Insurance", "Marine Insurance", "Travel Insurance", "Financial Planning"] },
  pune: { name: "Pune", state: "Maharashtra", description: "Pune's growing tech and manufacturing sectors need robust insurance coverage. We provide expert advisory for Pune professionals and businesses alike.", services: ["Health Insurance", "Car Insurance", "Term Insurance", "Corporate Insurance", "SIP Investments"] },
  bangalore: { name: "Bangalore", state: "Karnataka", description: "India's Silicon Valley deserves cutting-edge financial advisory. ExpertAdvise.in helps Bangalore tech professionals optimize insurance and investments.", services: ["Health Insurance", "Term Insurance", "Mutual Funds", "Retirement Planning", "Financial Planning"] },
  hyderabad: { name: "Hyderabad", state: "Telangana", description: "Hyderabad's booming pharma and IT sectors create unique insurance needs. Our advisors understand local requirements and provide tailored solutions.", services: ["Health Insurance", "Group Health Insurance", "Term Insurance", "Mutual Funds", "Car Insurance"] },
  chennai: { name: "Chennai", state: "Tamil Nadu", description: "Chennai's manufacturing and automotive hub needs specialized coverage. We serve individuals and enterprises across Tamil Nadu's capital.", services: ["Health Insurance", "Car Insurance", "Marine Insurance", "Term Insurance", "Financial Planning"] },
  ahmedabad: { name: "Ahmedabad", state: "Gujarat", description: "Gujarat's commercial capital with a strong entrepreneurial spirit. ExpertAdvise.in supports Ahmedabad's businesses and families with expert advisory.", services: ["Health Insurance", "Corporate Insurance", "Term Insurance", "Mutual Funds", "Travel Insurance"] },
  jaipur: { name: "Jaipur", state: "Rajasthan", description: "The Pink City's growing economy needs modern financial solutions. We bring expert insurance and investment advisory to Jaipur residents.", services: ["Health Insurance", "Term Insurance", "Car Insurance", "SIP Investments", "Retirement Planning"] },
  lucknow: { name: "Lucknow", state: "Uttar Pradesh", description: "Lucknow's emerging as a key Tier-2 city with growing insurance awareness. We help Lucknow families secure their future with the right plans.", services: ["Health Insurance", "Term Insurance", "Life Insurance", "Mutual Funds", "Car Insurance"] },
};

export default function CityPage() {
  const { city } = useParams<{ city: string }>();
  const data = city ? cityData[city] : null;
  const baseUrl = siteUrl;

  if (!data) {
    const pageUrl = `${baseUrl}/cities/${city}`;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <SEO
          title="City Page Not Found | ExpertAdvise.in"
          description="We do not have a dedicated city page yet. Browse ExpertAdvise.in for insurance and financial advisory services across India."
          noindex
          schemas={[webpageSchema("City Page Not Found", "We do not have a dedicated city page yet.", pageUrl)]}
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">City Page Not Found</h1>
          <p className="text-slate-500 mb-4">We do not have a dedicated page for this city yet.</p>
          <Link to="/" className="text-primary-600 font-semibold hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const pageUrl = `${baseUrl}/cities/${city}`;
  const schemas = [
    webpageSchema(
      `Insurance & Financial Advisory in ${data.name} | ExpertAdvise.in`,
      `${data.description} Get expert insurance advice, compare plans, and book free consultations in ${data.name}, ${data.state}.`,
      pageUrl
    ),
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: data.name, url: pageUrl },
    ]),
  ];

  const serviceIcons: Record<string, React.ElementType> = {
    "Health Insurance": Heart,
    "Term Insurance": Star,
    "Life Insurance": Star,
    "Car Insurance": Car,
    "Travel Insurance": Plane,
    "Marine Insurance": Building2,
    "Corporate Insurance": Building2,
    "Group Health Insurance": Heart,
    "Mutual Funds": Star,
    "SIP Investments": Star,
    "Retirement Planning": Star,
    "Financial Planning": Star,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`Insurance & Financial Advisory in ${data.name} | ExpertAdvise.in`}
        description={`${data.description} Get expert insurance advice, compare plans, and book free consultations in ${data.name}, ${data.state}.`}
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: data.name },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-white/15 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Local Advisory
            </span>
            <h1 className="text-2xl md:text-4xl font-bold mb-4">
              Insurance & Financial Advisory in {data.name}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mb-6">
              {data.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/book-appointment?source=Cities%20Page"
                className="bg-white text-primary-700 hover:bg-white/90 font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap flex items-center gap-2"
              >
                Book Free Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+919999365655"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                +91 99993 65655
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">
                  Why Choose ExpertAdvise.in in {data.name}?
                </h2>
                <div className="space-y-3">
                  {[
                    `Local market expertise for ${data.name} and ${data.state} residents`,
                    "Compare 25+ insurance companies side-by-side",
                    "Certified advisors with 10+ years average experience",
                    "97.8% claim assistance success rate",
                    "Free consultation — no obligation, no pressure",
                    `Serving ${data.name} customers since 2018`,
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">
                  Popular Services in {data.name}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.services.map((service) => {
                    const Icon = serviceIcons[service] || Star;
                    const route = service.toLowerCase().replace(/\s+/g, "-");
                    return (
                      <Link
                        key={service}
                        to={`/${route}`}
                        className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-primary-50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-primary-600" />
                        </div>
                        <span className="text-sm font-semibold text-slate-900">{service}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Contact {data.name} Office</h3>
                <div className="space-y-4 text-sm">
                  <a href="tel:+919999365655" className="flex items-center gap-3 text-slate-700 hover:text-primary-600 transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Phone</div>
                      <div className="font-medium">+91 99993 65655</div>
                    </div>
                  </a>
                  <a href="mailto:info@expertadvise.in" className="flex items-center gap-3 text-slate-700 hover:text-primary-600 transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Email</div>
                      <div className="font-medium">info@expertadvise.in</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3 text-slate-700">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Location</div>
                      <div className="font-medium">{data.name}, {data.state}, India</div>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/book-appointment?source=Cities%20Page"
                className="block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-4 rounded-xl transition-colors text-center whitespace-nowrap"
              >
                Book Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Get Expert Advice in {data.name} Today
          </h2>
          <p className="text-slate-600 mb-6">
            Join thousands of {data.name} residents who trust ExpertAdvise.in for their insurance and financial planning needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/book-appointment?source=Cities%20Page"
              className="bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
            >
              Schedule a Call
            </Link>
            <Link
              to="/compare-plans"
              className="bg-white hover:bg-slate-50 text-slate-700 font-semibold px-6 py-3 rounded-full border border-slate-200 transition-colors whitespace-nowrap"
            >
              Compare Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}